import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  Checkbox,
  Drawer,
  FormField,
  Heading,
  Select,
  Sentiments,
  Sizes,
  TextInput,
  TextTypes,
  Variants
} from "@sede-x/shell-ds-react-framework";
import { TCIQOption, TGenericProductsFilter, TProductsFetchData } from "carbonIQ/carbonIQtypes";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse } from "types";

const carbonGenericProductsFilterBy = loader(
  "../graphql/query-CarbonGenericProductsBlendFilterBy.graphql"
);

const ciAddNewProduct = loader("../graphql/mutation-CarbonAddNewProduct.graphql");

const ciUpdateProduct = loader("../graphql/mutation-CarbonUpdateProduct.graphql");

type TGenericProductsFilterByResponse = GqlResponse<
  TGenericProductsFilter[],
  "ciGenericProductsFilterBy"
>;

const AddOrUpdateProduct = ({
  details,
  onClose,
  onSubmit
}: {
  details: TProductsFetchData;
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const [genericProductList, setGenericProductList] = useState<TCIQOption[]>([]);

  // Fetch Generic Products
  const { loading: lCIQDocument, error: eCIQDocument } =
    useQuery<TGenericProductsFilterByResponse>(carbonGenericProductsFilterBy, {
      onCompleted: data => {
        setGenericProductList(
          data.ciGenericProductsFilterBy.map((prod: TGenericProductsFilter) => ({
            value: prod.name,
            label: prod.name,
            key: prod.id
          }))
        );
      }
    });

  // Add New Product
  const [addNewProduct, { loading: addProductLoading, error: addProductError }] =
    useMutation(ciAddNewProduct);

  // Update New Product
  const [updateProduct, { loading: updateProductLoading, error: updateProductError }] =
    useMutation(ciUpdateProduct);

  const isStateAdd = !details?.id;

  const [preFillProductDetails, setPreFillProductDetails] = useState<TProductsFetchData>(
    isStateAdd ? { active: true } : details
  );

  const isLoading = [lCIQDocument, updateProductLoading, addProductLoading].some(elm => elm);

  const onSubmitClick = () => {
    const tempdata = {
      active: !!preFillProductDetails.active,
      genericProductId: preFillProductDetails.genericProduct?.id,
      gsapCode: preFillProductDetails.gsapCode?.trim(),
      gsapName: preFillProductDetails.gsapName?.trim()
    };

    if (isStateAdd) {
      addNewProduct({
        variables: {
          product: tempdata
        },
        onCompleted: onSubmit
      });
    } else {
      updateProduct({
        variables: {
          product: {
            ...tempdata,
            id: preFillProductDetails.id,
            version: preFillProductDetails.version
          }
        },
        onCompleted: onSubmit
      });
    }
  };

  const isError = [addProductError, updateProductError, eCIQDocument].some(e => e);
  const errors = [addProductError, updateProductError, eCIQDocument].filter(
    (e): e is ApolloError => Boolean(e)
  );
  return (
    <Drawer
      header={
        <Heading className="setting-form-heading" type={TextTypes.H2}>
          {(isStateAdd ? "Add New" : "Update") + " Product"}
        </Heading>
      }
      borders={false}
      closeButton={false}
      sticky
      mask={true}
      open={true}
      maskClosable={false}
      size={Sizes.Medium}
      onClose={onClose}
      actions={[
        {
          label: "Cancel",
          action: onClose,
          props: {
            variant: Variants.Outlined
          }
        },
        {
          label: "Save",
          action: onSubmitClick,
          props: {
            disabled:
              !preFillProductDetails?.genericProduct ||
              !preFillProductDetails?.gsapCode ||
              !preFillProductDetails?.gsapName
          }
        }
      ]}>
      {!isStateAdd && <p>Update product</p>}
      {isLoading && <LoadingPanel />}
      {isError && <ApolloErrorViewer error={errors} />}

      <form>
        <FormField
          size={"medium"}
          id="gsap-code-label"
          mandatory={true}
          label="GSAP Code"
          bottomLeftHelper={{
            content: !preFillProductDetails?.gsapCode ? <b>Field can't be blank.</b> : "",
            sentiment: Sentiments.Negative
          }}>
          <TextInput
            onChange={event => {
              setPreFillProductDetails({
                ...preFillProductDetails,
                gsapCode: event.target.value.trim()
              });
            }}
            value={preFillProductDetails?.gsapCode ?? ""}
            invalid={!preFillProductDetails?.gsapCode}
          />
        </FormField>
        <FormField
          size={"medium"}
          id="gsap-name-label"
          label="GSAP Name"
          mandatory={true}
          bottomLeftHelper={{
            content: !preFillProductDetails?.gsapName ? <b>Field can't be blank.</b> : "",
            sentiment: Sentiments.Negative
          }}>
          <TextInput
            onChange={event => {
              setPreFillProductDetails({
                ...preFillProductDetails,
                gsapName: event?.target?.value
              });
            }}
            value={preFillProductDetails?.gsapName ?? ""}
            invalid={!preFillProductDetails?.gsapName}
          />
        </FormField>
        <FormField
          size={"medium"}
          id="generic-product-name-label"
          label="Generic Product Name"
          mandatory={true}
          bottomLeftHelper={{
            content: !preFillProductDetails?.genericProduct ? <b>Field can't be blank.</b> : "",
            sentiment: Sentiments.Negative
          }}>
          <Select
            options={genericProductList}
            size={"medium"}
            invalid={!preFillProductDetails?.genericProduct}
            id="generic-product-name-input"
            placeholder="Select a product"
            optionLabelProp="label"
            filterOption={true}
            optionFilterProp="label"
            labelInValue={true}
            value={preFillProductDetails?.genericProduct?.name ?? undefined}
            onChange={data => {
              setPreFillProductDetails({
                ...preFillProductDetails,
                genericProduct: !data ? data : { id: data.key, name: data.value }
              });
            }}
          />
        </FormField>
        <Checkbox
          label="Active"
          size={Sizes.Large}
          checked={preFillProductDetails?.active}
          onChange={() => {
            setPreFillProductDetails({
              ...preFillProductDetails,
              active: !preFillProductDetails?.active
            });
          }}
        />
      </form>
    </Drawer>
  );
};

export default AddOrUpdateProduct;
